import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { DeliveryArea, loadActiveAreas } from "../../../services/deliveryAreaService";
import { areaCharge, isFreeDelivery } from "../../../utils/deliveryCharge";
import { formatCurrencyBDT } from "../../../utils/currency";
import { ConfirmChangeDialog, ChangeRow } from "./ConfirmChangeDialog";
import { balanceText, codWarning, itemsTotalOf, MoneyImpact, round2 } from "./orderMath";
import { LockReason } from "./quickEditRules";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

export type ChargeField = "charge" | "discount" | "cost";

interface OrderChargesDialogProps {
    open: boolean;
    /** Which field gets the cursor. */
    focus: ChargeField;
    order: Order;
    /** Net amount paid so far. */
    paid: number;
    /** Locks the delivery charge and the discount (both change the total). */
    chargeLocked: LockReason;
    costLocked: LockReason;
    onClose: () => void;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

/** What the confirmation shows and saves — frozen when Review is pressed. */
interface Review {
    rows: ChangeRow[];
    impact: MoneyImpact | null;
    warnings: string[];
    payload: { shipping_amount?: number; discount_amount?: number; delivery_cost?: number };
    message: string;
}

/** "" or anything negative/non-numeric → NaN; otherwise rounded to paisa. */
const parseMoney = (text: string) => {
    if (text.trim() === "") return NaN;
    const n = Number(text);
    return Number.isFinite(n) && n >= 0 ? round2(n) : NaN;
};

/**
 * The order's money adjustments in one place: delivery charge (customer
 * pays), discount, and delivery cost (we pay). Review, then confirm. The
 * server re-derives the total from items + charge − discount.
 */
export const OrderChargesDialog: React.FC<OrderChargesDialogProps> = ({
    open,
    focus,
    order,
    paid,
    chargeLocked,
    costLocked,
    onClose,
    onOrderChanged,
    notify,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [chargeText, setChargeText] = useState("");
    const [discountText, setDiscountText] = useState("");
    const [costText, setCostText] = useState("");
    const [areas, setAreas] = useState<DeliveryArea[]>([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [review, setReview] = useState<Review | null>(null);
    const { saving, error, setError, save } = useQuickEditSave(order.order_id, onOrderChanged, notify);
    const chargeRef = useRef<HTMLInputElement>(null);
    const discountRef = useRef<HTMLInputElement>(null);
    const costRef = useRef<HTMLInputElement>(null);

    // Put the cursor in the field whose pencil was clicked, once the dialog
    // has finished opening (an autoFocus would lose to the dialog's own
    // focus handling), with the amount selected so typing replaces it.
    const focusChosenField = () => {
        const el = { charge: chargeRef, discount: discountRef, cost: costRef }[focus].current;
        if (el && !el.disabled) {
            el.focus();
            el.select();
        }
    };

    const oldCharge = round2(Number(order.shipping_amount ?? 0));
    const oldDiscount = round2(Number(order.discount_amount ?? 0));
    const oldCost = round2(Number(order.delivery_cost ?? 0));

    useEffect(() => {
        if (!open) return;
        setChargeText(String(oldCharge));
        setDiscountText(String(oldDiscount));
        setCostText(String(oldCost));
        setConfirmOpen(false);
        setError("");
        if (!chargeLocked) loadActiveAreas().then(setAreas, () => setAreas([]));
        // Reset only when the dialog opens, not on every refresh behind it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const charge = parseMoney(chargeText);
    const discount = parseMoney(discountText);
    const cost = parseMoney(costText);
    const itemsTotal = itemsTotalOf(order);
    const oldTotal = round2(Number(order.total_amount));

    const chargeChanged = !chargeLocked && !Number.isNaN(charge) && charge !== oldCharge;
    const discountChanged = !chargeLocked && !Number.isNaN(discount) && discount !== oldDiscount;
    const costChanged = !costLocked && !Number.isNaN(cost) && cost !== oldCost;
    const totalChanges = chargeChanged || discountChanged;
    const newTotal = totalChanges
        ? round2(itemsTotal + (chargeChanged ? charge : oldCharge) - (discountChanged ? discount : oldDiscount))
        : oldTotal;
    const discountTooBig = totalChanges && newTotal < 0;
    const valid = !Number.isNaN(charge) && !Number.isNaN(discount) && !Number.isNaN(cost) && !discountTooBig;

    const presets = useMemo(
        () =>
            areas.map((area) => {
                const free = isFreeDelivery(area, itemsTotal);
                return {
                    id: area.delivery_area_id,
                    amount: areaCharge(area, itemsTotal),
                    label: free
                        ? `${area.name} · Free (over ${formatCurrencyBDT(Number(area.free_above_amount))})`
                        : `${area.name} · ${formatCurrencyBDT(Number(area.delivery_charge))}`,
                };
            }),
        [areas, itemsTotal],
    );

    const openReview = () => {
        const rows: ChangeRow[] = [];
        if (chargeChanged) {
            rows.push({ label: "Delivery charge (customer pays)", from: formatCurrencyBDT(oldCharge), to: formatCurrencyBDT(charge) });
        }
        if (discountChanged) {
            rows.push({ label: "Discount", from: formatCurrencyBDT(oldDiscount), to: formatCurrencyBDT(discount) });
        }
        if (costChanged) {
            rows.push({ label: "Delivery cost (we pay)", from: formatCurrencyBDT(oldCost), to: formatCurrencyBDT(cost) });
        }
        const changedNames = [
            chargeChanged && "delivery charge",
            discountChanged && "discount",
            costChanged && "delivery cost",
        ].filter(Boolean) as string[];
        const changedList =
            changedNames.length === 1
                ? changedNames[0]
                : `${changedNames.slice(0, -1).join(", ")} and ${changedNames[changedNames.length - 1]}`;
        const cod = totalChanges ? codWarning(order, newTotal) : null;
        setReview({
            rows,
            impact: totalChanges ? { oldTotal, newTotal, paid } : null,
            warnings: cod ? [cod] : [],
            payload: {
                ...(chargeChanged ? { shipping_amount: charge } : {}),
                ...(discountChanged ? { discount_amount: discount } : {}),
                ...(costChanged ? { delivery_cost: cost } : {}),
            },
            message: `${changedList.charAt(0).toUpperCase()}${changedList.slice(1)} updated`,
        });
        setError("");
        setConfirmOpen(true);
    };

    const confirm = async () => {
        if (!review) return;
        const saved = await save(() => orderService.updateOrderCharges(order.order_id, review.payload), review.message);
        if (saved) {
            setConfirmOpen(false);
            onClose();
        }
    };

    const moneyField = (
        label: string,
        value: string,
        setValue: (v: string) => void,
        locked: LockReason,
        inputRef: React.RefObject<HTMLInputElement | null>,
        hint: string,
        extraError?: string,
    ) => {
        const invalid = !locked && Number.isNaN(parseMoney(value));
        return (
            <TextField
                label={label}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                type="number"
                fullWidth
                inputRef={inputRef}
                disabled={!!locked}
                error={invalid || !!extraError}
                helperText={locked ?? (invalid ? "Enter an amount of 0 or more" : extraError ?? hint)}
                InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
                inputProps={{ min: 0, step: 1, inputMode: "decimal" }}
            />
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="xs"
                slotProps={{ transition: { onEntered: focusChosenField } }}
            >
                <DialogTitle>Charges and discount</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                        <Box>
                            {moneyField(
                                "Delivery charge",
                                chargeText,
                                setChargeText,
                                chargeLocked,
                                chargeRef,
                                "What the customer pays — part of the order total",
                            )}
                            {!chargeLocked && presets.length > 0 && (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                    {presets.map((p) => (
                                        <Chip
                                            key={p.id}
                                            label={p.label}
                                            size="small"
                                            variant={charge === p.amount ? "filled" : "outlined"}
                                            color={charge === p.amount ? "primary" : "default"}
                                            onClick={() => setChargeText(String(p.amount))}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                        {moneyField(
                            "Discount",
                            discountText,
                            setDiscountText,
                            chargeLocked,
                            discountRef,
                            "Taken off the order total",
                            discountTooBig ? "The discount can't be more than the items plus the delivery charge" : undefined,
                        )}
                        {moneyField(
                            "Delivery cost",
                            costText,
                            setCostText,
                            costLocked,
                            costRef,
                            "What we pay the courier — affects profit only",
                        )}
                        <Box sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1.5, py: 1 }}>
                            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                Order total{" "}
                                <strong>
                                    {totalChanges && !discountTooBig ? `${formatCurrencyBDT(oldTotal)} → ` : ""}
                                    {formatCurrencyBDT(discountTooBig ? oldTotal : newTotal)}
                                </strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Paid {formatCurrencyBDT(paid)} · {balanceText(discountTooBig ? oldTotal : newTotal, paid)}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!valid || (!chargeChanged && !discountChanged && !costChanged)}
                        onClick={openReview}
                    >
                        Review
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmChangeDialog
                open={confirmOpen}
                title="Save charges?"
                rows={review?.rows ?? []}
                impact={review?.impact}
                warnings={review?.warnings}
                error={error}
                saving={saving}
                onConfirm={confirm}
                onCancel={() => setConfirmOpen(false)}
            />
        </>
    );
};
